import type { PartnerMapContent, PartnerTheme } from "../maps/types";
import { COUNTRIES, HOSTELS, hostelPhotoPaths, MAP_HOSTELS } from "./content";
import { mmUrl } from "./links";
import { mapHead } from "./routeHead";

/**
 * The Mad Monkey hostels' maps, `/p/mad-monkey-<hostel>`: one `PartnerMapContent` per hostel in `mapHostels` of
 * madMonkeyData.json, made from the hostel's data (name, coordinates, tagline, photos, country) and one shared look. Not
 * a JSON file each: the places come from the hostel's synced Stippl list (`partners/maps/liveHighlights.ts`) and what is on
 * from its program (`hostelProgram.ts`), both laid over this by the page, and until they exist the page is the hostel,
 * its photos and its links, with no highlights and no Week program.
 *
 * Black and lime, Poppins and Rubik, as in the design: the template's `lit` look. No prices anywhere on these maps (Ilse,
 * 17 Oct), and nothing here carries one.
 */

/** the media folder every Mad Monkey page shares (`madmonkey/assets.ts`); the paths below are under it */
const MEDIA = "mad-monkey";

const FONTS = '"Poppins", system-ui, -apple-system, "Segoe UI", sans-serif';

const MAD_MONKEY_THEME: PartnerTheme = {
	primary: "#000000",
	primaryHover: "#1d2326",
	tint: "#ccff01",
	cream: "#ccff01",
	accent: "#000000",
	lit: "#ccff01",
	fonts: {
		href: "https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800&family=Rubik:wght@500;600;700&display=swap",
		head: FONTS,
		ui: FONTS,
	},
};

/** The zoom every close-up on a hostel's map is measured from (the map itself opens on its 3 nearest highlights, `openingKm`). */
export const HOSTEL_ZOOM = 14;

/** How a place beyond a walk is reached, by country: a Grab everywhere but Laos, where it is a tuk-tuk */
const RIDE: Record<string, string> = { la: "by tuk-tuk" };
const DEFAULT_RIDE = "by Grab";

/** what the times and bookings of a Mad Monkey map are, after "Map made with Stippl, the free trip planner, for <hostel>." */
const SMALL_PRINT = "Events and times come from Mad Monkey and can change, so the bar has the latest. Beds and events are booked directly with Mad Monkey.";

/** Directions to a point in Google Maps, at its coordinates. */
const googleMapsAt = (lat: number, lng: number): string => `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;

/** `/p/mad-monkey-<slug>`: the map of a hostel that has one. */
export function hostelMap(slug: string): PartnerMapContent {
	const hostel = HOSTELS[slug];
	const photos = hostelPhotoPaths(slug);
	const head = mapHead(slug)!;
	return {
		slug: `mad-monkey-${slug}`,
		media: MEDIA,
		head: { title: head.title, description: head.description, ogImage: photos[0] },
		partner: {
			name: `Mad Monkey ${hostel.name}`,
			shortName: "Mad Monkey",
			url: mmUrl(slug),
			// no number: the card has "Book & info" where other partners have the contact row
			tel: "",
			telDisplay: "",
			whatsapp: "",
			username: "madmonkey",
			logo: "img/mm-monkey.png",
		},
		header: { title: `${hostel.name}, the Mad Monkey way`, subtitle: "Highlights picked by the crew", mapLabel: `Map of ${hostel.name}` },
		theme: MAD_MONKEY_THEME,
		map: { lng: hostel.lon, lat: hostel.lat, zoom: HOSTEL_ZOOM, openAround: true },
		rideLabel: RIDE[hostel.cc] ?? DEFAULT_RIDE,
		// What the page can say before the hostel's program is there: book on the hostel's own page, and the way to it.
		info: {
			book: mmUrl(slug),
			directions: googleMapsAt(hostel.lat, hostel.lon),
			followLabel: "Follow the chaos",
			followHandle: "@madmonkeyhostels",
			socials: [],
		},
		smallPrint: SMALL_PRINT,
		base: {
			area: hostel.name,
			address: `${hostel.name}, ${COUNTRIES[hostel.cc]}`,
			tagline: hostel.tag,
			lat: hostel.lat,
			lng: hostel.lon,
			directionsUrl: googleMapsAt(hostel.lat, hostel.lon),
			photos,
		},
		highlights: [],
		venues: [],
		cta: { listId: "", campaign: { pid: "madmonkey", prefix: "mm" } },
	};
}

export const MAD_MONKEY_MAPS: PartnerMapContent[] = MAP_HOSTELS.map(hostelMap);
